import { DefaultError } from "./Notification/DefaultError";
import { NoNotification } from "./Notification/NoNotification";
import { DefaultNotification } from "./Notification/DefaultNotification";
import { MultiErrorMessageNotification, type ErrorGroup } from "./Notification/MultiErrorMessageNotification";
import { NotificationTypes } from "./Notification/NotificationTypes";
import { ToastrAdapter } from "./Toastr/ToastrAdapter";
import {catchError} from "../../../Utilities/Api/Requests/requestUtilities";
import type { Notification } from "./Notification/Notification";

export class NotificationFactory {
	private _notificationAdapter: ToastrAdapter;

	constructor() {
		this._notificationAdapter = new ToastrAdapter();
	}

	clear(clearErrorWarningToastsOnly = false): void {
		return this._notificationAdapter.clear(clearErrorWarningToastsOnly);
	}

	createNone(): Notification {
		return new NoNotification();
	}

	createSuccess(message: string): Notification {
		return new DefaultNotification(this._notificationAdapter, NotificationTypes.SUCCESS, message);
	}

	createWarning(message: string): Notification {
		return new DefaultNotification(this._notificationAdapter, NotificationTypes.WARNING, message);
	}

	createError(message: string): Notification {
		return new DefaultNotification(this._notificationAdapter, NotificationTypes.ERROR, message);
	}

	createErrorsFromXhrObject(xhrObject: unknown): Notification {
		const xhr = xhrObject as Record<string, unknown>;
		if (xhr.status === 403)
			return new DefaultNotification(
				this._notificationAdapter,
				NotificationTypes.ERROR,
				"Please contact support to verify your access to this section.",
			);
		let errorResponseObject: Record<string, unknown> | undefined;

		try {
			errorResponseObject = JSON.parse(xhrObject as string);
			if (errorResponseObject && errorResponseObject.responseText === "" && (errorResponseObject.totalErrors as number) < 1) return new NoNotification();
		} catch (e) {
			catchError("The server did not return a valid error object.");
			return new DefaultError(this._notificationAdapter);
		}

		if (!errorResponseObject || !errorResponseObject.errors) return new DefaultError(this._notificationAdapter);

		let notification: Notification;
		const errors = errorResponseObject.errors as { message: string }[];
		let totalErrors = errorResponseObject.totalErrors as number;

		if (totalErrors === 1)
			notification = new DefaultNotification(
				this._notificationAdapter,
				NotificationTypes.ERROR,
				errors[0].message,
			);
		else if (totalErrors > 1)
			notification = new MultiErrorMessageNotification(this._notificationAdapter, errors as unknown as ErrorGroup[]);
		else notification = new NoNotification();

		return notification;
	}

	createInfo(message: string): Notification {
		return new DefaultNotification(this._notificationAdapter, NotificationTypes.INFO, message);
	}
}
